import {
  Box,
  Container,
  LoadingOverlay,
  Stack,
  Tabs,
} from "@mantine/core";
import { useState } from "react";
import Confetti from "react-confetti";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { Brand, User } from "../../../gql/__generated__/graphql";
import { userInfoAtom } from "../../atoms";
import FullPageMessage from "../../components/FullPageMessage/FullPageMessage";
import PageHeader from "../../components/PageHeader/PageHeader";
import ReviewModal from "../../components/Modals/ReviewModal/ReviewModal";
import { confettiCollabsAtom } from "./atoms";
import classes from "./CollabsPage.module.css";
import CollabsList from "./components/CollabsList";
import { useCollabs } from "./useCollabs";

function CollabsPage() {
  const navigate = useNavigate();

  // Recoil state
  const userInfo = useRecoilValue(userInfoAtom);
  const confettiCollabs = useRecoilValue(confettiCollabsAtom);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [entityForReviewModal, setEntityForReviewModal] = useState<
    User | Brand | undefined
  >(undefined);

  const handleOpenReviewModal = (entity: User | Brand) => {
    setEntityForReviewModal(entity);
    setReviewModalOpen(true);
  };

  if (!userInfo) {
    return (
      <FullPageMessage
        title="Authentication Required"
        message="You must be logged in to view this page."
        type="warning"
        actionButton={{
          label: "Sign In",
          onClick: () => navigate("/login"),
        }}
      />
    );
  }

  // Collabs state
  const {
    pendingCollabs,
    acceptedCollabs,
    reviewingCollabs,
    historyCollabs,
    actionRequiredCounts,
    loading,
    error,
  } = useCollabs(userInfo.userType);

  // Handle loading and error states
  if (loading) return <LoadingOverlay visible={true} />;
  if (error)
    return (
      <FullPageMessage
        title="An error ocurred while fetching collabs."
        message="Please try again, or contact us if this persists."
        type="error"
      />
    );

  return (
    <Box data-component="CollabsPage">
      {confettiCollabs && <Confetti />}
      <PageHeader title="Collabs" subtitle="Manage your collabs." />

      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Tabs defaultValue="pending" variant="outline">
            <Tabs.List grow>
              <Tabs.Tab
                value="pending"
                rightSection={
                  actionRequiredCounts.pending > 0 ? (
                    <Box className={classes.notificationBadge}>
                      {actionRequiredCounts.pending}
                    </Box>
                  ) : null
                }
              >
                Pending
              </Tabs.Tab>
              <Tabs.Tab
                value="accepted"
                rightSection={
                  actionRequiredCounts.accepted > 0 ? (
                    <Box className={classes.notificationBadge}>
                      {actionRequiredCounts.accepted}
                    </Box>
                  ) : null
                }
              >
                Accepted
              </Tabs.Tab>
              <Tabs.Tab
                value="reviewing"
                rightSection={
                  actionRequiredCounts.reviewing > 0 ? (
                    <Box className={classes.notificationBadge}>
                      {actionRequiredCounts.reviewing}
                    </Box>
                  ) : null
                }
              >
                Reviewing
              </Tabs.Tab>
              <Tabs.Tab
                value="history"
                rightSection={
                  actionRequiredCounts.history > 0 ? (
                    <Box className={classes.notificationBadge}>
                      {actionRequiredCounts.history}
                    </Box>
                  ) : null
                }
              >
                History
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="pending" pt="xl">
              <CollabsList
                collabs={pendingCollabs}
                role={userInfo.userType}
                onReview={handleOpenReviewModal}
              />
            </Tabs.Panel>

            <Tabs.Panel value="accepted" pt="xl">
              <CollabsList
                collabs={acceptedCollabs}
                role={userInfo.userType}
                onReview={handleOpenReviewModal}
              />
            </Tabs.Panel>

            <Tabs.Panel value="reviewing" pt="xl">
              <CollabsList
                collabs={reviewingCollabs}
                role={userInfo.userType}
                onReview={handleOpenReviewModal}
              />
            </Tabs.Panel>

            <Tabs.Panel value="history" pt="xl">
              <CollabsList
                collabs={historyCollabs}
                role={userInfo.userType}
                onReview={handleOpenReviewModal}
              />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
      {entityForReviewModal && (
        <ReviewModal
          opened={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          entityType={"CREATOR"}
          entityName={entityForReviewModal.name || ""}
          entityId={
            (entityForReviewModal as User).email ??
            (entityForReviewModal as Brand).id
          }
        />
      )}
    </Box>
  );
}

export default CollabsPage;
